/**
 * Full-precision formatting for the claim-review/casework pages moved in
 * from the Workbench app (Claim Queue, Claim Detail, Matter Tracker,
 * Search, Tabular Review, Dispute Thread). Deliberately separate from
 * `./format.ts` (this app's own compact "₹X Cr/L" convention used by the
 * Overview/Pipeline/Exposure pages) rather than merged into it - these two
 * conventions render real currency amounts differently on purpose (a
 * reviewer working one claim's exact figures wants the full rupee string,
 * an executive scanning a KPI tile wants the compact one) and unifying
 * them would change either the reviewer pages' already-tested behavior or
 * the executive pages' already-shipped look.
 */
import type { AuthorityStatusValue, ClaimCandidateStatus, Forum } from "@hp/types";

const INR_FORMATTER = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

export function formatINR(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return "—";
  return INR_FORMATTER.format(amount);
}

export function formatValueRange(low: number, high: number): string {
  if (low === high) return formatINR(low);
  return `${formatINR(low)} – ${formatINR(high)}`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso.length <= 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `${Math.round(value * 100)}%`;
}

/** "change_in_law_coal_cess" -> "Change In Law Coal Cess" */
export function humanizeSnakeCase(value: string): string {
  return value
    .split("_")
    .map((word) => (word.length === 0 ? word : word[0].toUpperCase() + word.slice(1)))
    .join(" ");
}

const FORUM_LABELS: Record<Forum, string> = {
  central_tribunal: "Central Tribunal",
  appellate_tribunal: "Appellate Tribunal",
  supreme_court: "Supreme Court",
  state_commission: "State Commission",
  central_ministry: "Central Ministry",
};

/** Displays a Forum enum value, falling back to a humanized form for any other string. */
export function humanizeForum(forum: string): string {
  return FORUM_LABELS[forum as Forum] ?? humanizeSnakeCase(forum);
}

export function humanizeIssueTag(issueTag: string): string {
  return humanizeSnakeCase(issueTag);
}

const CLAIM_STATUS_LABELS: Record<ClaimCandidateStatus, string> = {
  identified: "Identified",
  validated: "Validated",
  filed: "Filed",
  allowed: "Allowed",
  recovered: "Recovered",
  rejected: "Rejected",
  deferred: "Deferred",
};

export function humanizeClaimStatus(status: ClaimCandidateStatus): string {
  return CLAIM_STATUS_LABELS[status] ?? humanizeSnakeCase(status);
}

const AUTHORITY_STATUS_LABELS: Record<AuthorityStatusValue, string> = {
  final: "Final",
  pending_appeal: "Pending Appeal",
  stayed: "Stayed",
  reversed: "Reversed",
  upheld: "Upheld",
  superseded: "Superseded",
};

export function humanizeAuthorityStatus(status: AuthorityStatusValue): string {
  return AUTHORITY_STATUS_LABELS[status] ?? humanizeSnakeCase(status);
}

const EDGE_TYPE_LABELS: Record<string, string> = {
  cites: "Cites",
  decides: "Decides",
  appeals: "Appealed",
  stays: "Stayed by",
  reverses: "Reversed by",
  upholds: "Upheld by",
  remands: "Remanded by",
  amends: "Amended by",
};

export function humanizeEdgeType(edgeType: string): string {
  return EDGE_TYPE_LABELS[edgeType] ?? humanizeSnakeCase(edgeType);
}
